import Vue from 'vue'
import { ClientOnly } from '~/decorators'
import { injectable } from '~/decorators/dependency-container'
import { MouseEventHandler } from '~/models/event'
import { DestroyListenersFunction, RefType } from '~/models/event/builder'
import { IEventBuilder } from '~/builders/event/IEventBuilder'

@ClientOnly
@injectable()
export default class HoverEventBuilder implements IEventBuilder {
  private elementOrElementGetter: RefType | (() => RefType) | undefined
  private componentInstance: Vue | undefined
  private mouseEnterHandler: MouseEventHandler | undefined
  private mouseLeaveHandler: MouseEventHandler | undefined
  private addOnNextTick: boolean = false

  setElement(
    elementOrElementGetter: RefType | (() => RefType)
  ): HoverEventBuilder {
    this.elementOrElementGetter = elementOrElementGetter
    return this
  }

  private get element(): RefType | undefined {
    if (this.elementOrElementGetter instanceof Function) {
      return this.elementOrElementGetter()
    }
    return this.elementOrElementGetter as RefType | undefined
  }

  setComponent(componentInstance: Vue): HoverEventBuilder {
    this.componentInstance = componentInstance
    return this
  }

  onNextTick(): HoverEventBuilder {
    this.addOnNextTick = true
    return this
  }

  onEnter(handler: MouseEventHandler): HoverEventBuilder {
    this.mouseEnterHandler = handler
    return this
  }

  onLeave(handler: MouseEventHandler): HoverEventBuilder {
    this.mouseLeaveHandler = handler
    return this
  }

  build(): DestroyListenersFunction {
    this.validateBuildPrerequisites()
    const componentInstance = this.componentInstance!
    const mouseEnterHandler = this.mouseEnterHandler!
    const mouseLeaveHandler = this.mouseLeaveHandler!

    const onNextTickOrImmediately: (fn: () => void) => void = this.addOnNextTick
      ? componentInstance.$nextTick
      : fn => {
          fn()
        }

    const removeListeners = () => {
      const element = this.element! as HTMLElement
      element.removeEventListener('mouseenter', mouseEnterHandler)
      element.removeEventListener('mouseleave', mouseLeaveHandler)
    }

    onNextTickOrImmediately(() => {
      const element = this.element! as HTMLElement
      element.addEventListener('mouseenter', mouseEnterHandler)
      element.addEventListener('mouseleave', mouseLeaveHandler)

      componentInstance.$once('hook:beforeDestroy', removeListeners)
    })

    return removeListeners
  }

  private validateBuildPrerequisites() {
    const prerequisites = [
      ['element', this.elementOrElementGetter],
      ['componentInstance', this.componentInstance],
      ['mouseEnterHandler', this.mouseEnterHandler],
      ['mouseLeaveHandler', this.mouseLeaveHandler]
    ]

    for (const [propertyName, prerequisite] of prerequisites) {
      if (!prerequisite) {
        throw new TypeError(`${propertyName} undefined`)
      }
    }
  }
}
